// CapabilitiesPage — /hub/admin/capabilities (Team & Admin §2).
//
// "What the company can do, and where it is fragile." Derived from 102,000
// completed tasks — nobody typed a profile, and a derived one cannot go stale.
// Tier 5 (raw and technical): tables are correct. One loud moment per screen:
// the single-points-of-failure list.
//
// Read-only, frontend-direct (CLIENTHUB_KNOWLEDGE_BASE.md §7): the page calls
// Supabase through src/api/hub/capabilities.js — components never touch
// supabase. There is NO BFF piece for this screen. Every read resolves
// auth.uid() itself — the database is the wall; the capability check below is a
// convenience, not the wall.
//
// Gated by agent.manage (the closest existing admin capability; a backend
// capability.manage is the eventual right gate — see MailboxHealthPage for the
// same transitional pattern). A viewer without it renders <NotPermitted>.
//
// Non-negotiables: render, don't compute (every number comes from the view);
// never hardcode a capability (all are rows and all grow); null is not zero (a
// null count reads "—", a zero reads "0" = "there is nothing"). Reasons from
// fn_who_can are rendered verbatim. Column readers fall back across plausible
// names — the catalogue has typos (genrate_google_ads_reports) and inconsistent
// categories; that is the data, shown as-is, never patched in the UI.

import React, { useCallback, useEffect, useState } from "react";
import { HubShell } from "../../components/hub/shell";
import { NotPermitted } from "../../components/hub/kit";
import { EmptyState, ErrorState } from "../../components/shared/StateMessage";
import PanelErrorBoundary from "../../components/shared/PanelErrorBoundary";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { fmtNumber, fmtDate } from "../../components/shared/format";
import {
  getCapabilityCoverage,
  getMemberCapability,
  getCapabilitiesCatalogue,
  whoCan,
} from "../../api/hub/capabilities";

// Defensive column readers — the views are verified, but degrade rather than
// crash on a drifted/missing field (mirrors InboxDrawer / MailboxHealthPage).
const capKeyOf = (r) => r?.capability_key ?? r?.capability ?? "—";
const capLabelOf = (r) => r?.label ?? r?.capability_key ?? r?.capability ?? "—";
const categoryOf = (r) => r?.category ?? null;
const countOr = (v) => (typeof v === "number" ? v : null);
const spofOf = (r) => !!r?.single_point_of_failure;
const nobodyRecentOf = (r) => !!r?.nobody_recent;

const memberNameOf = (r) => r?.person_name ?? r?.member_name ?? r?.name ?? "—";
const memberCapOf = (r) => r?.capability_key ?? r?.capability ?? "—";
const memberDepthOf = (r) => countOr(r?.depth);
const memberLastOf = (r) => r?.last_done ?? r?.last_completed ?? r?.last_done_at ?? null;
const memberTimesOf = (r) => countOr(r?.times_done);

const whoNameOf = (r) => r?.name ?? r?.person_name ?? r?.member_name ?? "—";
const whoKindOf = (r) =>
  r?.kind ?? (r?.is_agent || r?.type === "agent" ? "agent" : "person");
const whoReasonOf = (r) => r?.reason ?? r?.sentence ?? r?.why ?? null;

// A count cell: null → "—" (gated/unknown), 0 → "0" (nothing). Never conflated.
const CountCell = ({ value }) => {
  if (value == null) return <span className="text-gray-300 dark:text-gray-600">—</span>;
  return <span className="tabular-nums">{fmtNumber(value)}</span>;
};

export default function CapabilitiesPage() {
  const { can } = useWorkspace();
  const permitted = can("agent.manage");

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [coverage, setCoverage] = useState([]);
  const [memberCapability, setMemberCapability] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [pageError, setPageError] = useState(null);

  // Best-effort supplementary reads degrade inline (KB §10: one failing panel
  // never blanks the page). Coverage is the primary read — its failure is a
  // page-level error.
  const [memberError, setMemberError] = useState(null);
  const [catalogueError, setCatalogueError] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    setPageError(null);
    setMemberError(null);
    setCatalogueError(null);
    try {
      // One Promise.all per page load (KB §9). allSettled so a supplementary
      // view failing degrades inline instead of blanking the screen.
      const [cov, mem, cat] = await Promise.allSettled([
        getCapabilityCoverage(),
        getMemberCapability(),
        getCapabilitiesCatalogue(),
      ]);

      if (cov.status === "fulfilled") setCoverage(cov.value);
      else {
        setCoverage([]);
        setPageError(cov.reason?.message || "Couldn't load capability coverage.");
      }
      if (mem.status === "fulfilled") {
        setMemberCapability(mem.value);
        setMemberError(null);
      } else {
        setMemberCapability([]);
        setMemberError(mem.reason?.message || "Couldn't load who-can-do-what.");
      }
      if (cat.status === "fulfilled") {
        setCatalogue(cat.value);
        setCatalogueError(null);
      } else {
        setCatalogue([]);
        setCatalogueError(cat.reason?.message || "Couldn't load the catalogue.");
      }

      setStatus(cov.status === "fulfilled" ? "ready" : "error");
    } catch (e) {
      setPageError(e?.message || "Couldn't load capabilities.");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (permitted) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitted]);

  // ── "Who can" lookup (fn_who_can) ──────────────────────────────────────────
  const [whoQuery, setWhoQuery] = useState("");
  const [whoStatus, setWhoStatus] = useState("idle"); // idle | loading | done | error
  const [whoResults, setWhoResults] = useState([]);
  const [whoError, setWhoError] = useState(null);

  const runWhoCan = useCallback(
    async (e) => {
      e?.preventDefault();
      const capability = whoQuery.trim();
      if (!capability) {
        setWhoError("Type a capability to look up.");
        setWhoStatus("error");
        return;
      }
      setWhoStatus("loading");
      setWhoError(null);
      try {
        const rows = await whoCan({ capability });
        setWhoResults(Array.isArray(rows) ? rows : rows ? [rows] : []);
        setWhoStatus("done");
      } catch (err) {
        setWhoError(err?.message || "Couldn't look up who can.");
        setWhoResults([]);
        setWhoStatus("error");
      }
    },
    [whoQuery],
  );

  if (!permitted) {
    return (
      <HubShell>
        <div className="p-6">
          <NotPermitted message="Capabilities is an admin surface. You don't have access to this view." />
        </div>
      </HubShell>
    );
  }

  // Loud moment (Tier 5): the single-points-of-failure list.
  const spofs = coverage.filter(spofOf);
  const lost = coverage.filter(nobodyRecentOf);

  return (
    <HubShell>
      <div className="mx-auto max-w-6xl px-5 py-6">
        <header className="mb-5">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Capabilities
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            What the company can do, and where it is fragile. Derived from
            completed work — nobody typed a profile, and a derived one cannot go
            stale.
          </p>
        </header>

        {status === "loading" && (
          <div className="py-12 text-center text-sm text-gray-400">
            Loading capabilities…
          </div>
        )}

        {status === "error" && (
          <ErrorState
            variant="card"
            title="Capabilities are temporarily unavailable"
            message={pageError}
            onRetry={load}
          />
        )}

        {status === "ready" && (
          <div className="space-y-8">
{/* ── Loud moment: single points of failure ───────────────────── */}
            <PanelErrorBoundary>
              <section
                data-testid="spof-section"
                className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5 dark:border-amber-900/40 dark:bg-amber-900/10"
              >
                <h2 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Single points of failure
                </h2>
                <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-400/80">
                  A skill exactly one person holds. Invisible until they are on
                  leave — and by then it is a problem rather than a plan.
                </p>
                {spofs.length === 0 ? (
                  <p className="mt-3 text-sm text-amber-700/80 dark:text-amber-400/80">
                    No single points of failure today.
                  </p>
                ) : (
                  <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                    {spofs.map((r) => (
                      <li
                        key={capKeyOf(r)}
                        data-testid={`spof-${capKeyOf(r)}`}
                        className="rounded-lg border border-amber-200 bg-white px-3 py-2 dark:border-amber-900/40 dark:bg-gray-900"
                      >
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {capLabelOf(r)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {countOr(r?.people) === 1
                            ? "1 person · no agent"
                            : `${fmtNumber(r?.people ?? 0)} people · ${fmtNumber(r?.agents ?? 0)} agents`}
                          {categoryOf(r) ? ` · ${categoryOf(r)}` : ""}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </PanelErrorBoundary>

            {/* ── Skills the company has quietly lost ────────────────────── */}
            <PanelErrorBoundary>
              <section data-testid="nobody-recent-section">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Quietly lost
                </h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  A skill the company has nobody recent at — `nobody_recent` in
                  the coverage view.
                </p>
                {lost.length === 0 ? (
                  <p className="mt-2 text-sm text-gray-400">
                    Nothing flagged as quietly lost.
                  </p>
                ) : (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {lost.map((r) => (
                      <li
                        key={capKeyOf(r)}
                        className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
                      >
                        {capLabelOf(r)}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </PanelErrorBoundary>
{/* ── Who can … (fn_who_can) ─────────────────────────────────── */}
            <PanelErrorBoundary>
              <section data-testid="who-can-section">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Who can
                </h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Ask who can run a capability. People and agents appear on
                  identical terms — the profile is derived the same way.
                </p>
                <form onSubmit={runWhoCan} className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={whoQuery}
                    onChange={(e) => setWhoQuery(e.target.value)}
                    placeholder="e.g. google ad"
                    aria-label="Capability to look up"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-[#32B1FF] focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    data-testid="who-can-input"
                  />
                  <button
                    type="submit"
                    disabled={whoStatus === "loading"}
                    className="rounded-lg bg-[#32B1FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1a9aef] disabled:opacity-50"
                    data-testid="who-can-submit"
                  >
                    {whoStatus === "loading" ? "…" : "Ask"}
                  </button>
                </form>

                {whoStatus === "error" && (
                  <div className="mt-3">
                    <ErrorState variant="strip" message={whoError} />
                  </div>
                )}
                {whoStatus === "done" && whoResults.length === 0 && (
                  <div className="mt-3">
                    <EmptyState message="Nobody can run that — people or agents." />
                  </div>
                )}
                {whoStatus === "done" && whoResults.length > 0 && (
                  <ul
                    data-testid="who-can-results"
                    className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-800"
                  >
                    {whoResults.map((r, i) => (
                      <li
                        key={`${whoNameOf(r)}-${i}`}
                        data-testid={`who-can-result-${i}`}
                        className="flex items-start gap-3 px-3 py-2"
                      >
                        <span
                          className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                            whoKindOf(r) === "agent"
                              ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                          }`}
                        >
                          {whoKindOf(r) === "agent" ? "Agent" : "Person"}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {whoNameOf(r)}
                          </p>
                          {whoReasonOf(r) && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {whoReasonOf(r)}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </PanelErrorBoundary>
{/* ── Coverage table (v_capability_coverage) ─────────────────── */}
            <PanelErrorBoundary>
              <section data-testid="coverage-section">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Coverage
                </h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  One row per capability. Every number comes from the view —
                  nothing is computed in the browser.
                </p>
                {coverage.length === 0 ? (
                  <div className="mt-3">
                    <EmptyState message="No capabilities found." />
                  </div>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                          <th className="px-3 py-2">Capability</th>
                          <th className="px-3 py-2">Category</th>
                          <th className="px-3 py-2 text-right">People</th>
                          <th className="px-3 py-2 text-right">Agents</th>
                          <th className="px-3 py-2 text-right">Times done</th>
                          <th className="px-3 py-2">Flags</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {coverage.map((r) => (
                          <tr
                            key={capKeyOf(r)}
                            data-testid={`coverage-row-${capKeyOf(r)}`}
                            className="hover:bg-gray-50 dark:hover:bg-gray-900/50"
                          >
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {capLabelOf(r)}
                              </div>
                              <div className="text-xs text-gray-400">
                                {capKeyOf(r)}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                              {categoryOf(r) ?? "—"}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <CountCell value={countOr(r?.people)} />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <CountCell value={countOr(r?.agents)} />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <CountCell value={countOr(r?.times_done)} />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {spofOf(r) && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                    SPOF
                                  </span>
                                )}
                                {nobodyRecentOf(r) && (
                                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                    nobody recent
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </PanelErrorBoundary>
{/* ── Who can do what (mv_member_capability) ─────────────────── */}
            <PanelErrorBoundary>
              <section data-testid="member-capability-section">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Who can do what
                </h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Per person, per capability — with depth and recency.
                </p>
                {memberError ? (
                  <div className="mt-3">
                    <ErrorState variant="strip" message={memberError} />
                  </div>
                ) : memberCapability.length === 0 ? (
                  <div className="mt-3">
                    <EmptyState message="No per-person capability rows." />
                  </div>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                          <th className="px-3 py-2">Person</th>
                          <th className="px-3 py-2">Capability</th>
                          <th className="px-3 py-2 text-right">Depth</th>
                          <th className="px-3 py-2 text-right">Times</th>
                          <th className="px-3 py-2">Last done</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {memberCapability.map((r, i) => (
                          <tr
                            key={`${memberNameOf(r)}-${memberCapOf(r)}-${i}`}
                            data-testid={`member-cap-row-${i}`}
                          >
                            <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                              {memberNameOf(r)}
                            </td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                              {memberCapOf(r)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <CountCell value={memberDepthOf(r)} />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <CountCell value={memberTimesOf(r)} />
                            </td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                              {memberLastOf(r) ? fmtDate(memberLastOf(r)) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </PanelErrorBoundary>
{/* ── Catalogue (ops_capabilities) ───────────────────────────── */}
            <PanelErrorBoundary>
              <section data-testid="catalogue-section">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Catalogue
                </h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  The registry — a new capability is a row, not a deploy. Shown
                  as-is: typos and inconsistent categories are the data, never
                  patched in the UI.
                </p>
                {catalogueError ? (
                  <div className="mt-3">
                    <ErrorState variant="strip" message={catalogueError} />
                  </div>
                ) : catalogue.length === 0 ? (
                  <div className="mt-3">
                    <EmptyState message="No capabilities in the catalogue." />
                  </div>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
                      <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                          <th className="px-3 py-2">Capability</th>
                          <th className="px-3 py-2">Category</th>
                          <th className="px-3 py-2">Description</th>
                          <th className="px-3 py-2">Active</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {catalogue.map((r) => (
                          <tr
                            key={capKeyOf(r)}
                            data-testid={`catalogue-row-${capKeyOf(r)}`}
                          >
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {capLabelOf(r)}
                              </div>
                              <div className="text-xs text-gray-400">
                                {capKeyOf(r)}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                              {categoryOf(r) ?? "—"}
                            </td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                              {r?.description ?? "—"}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                  r?.is_active
                                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                    : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                                }`}
                              >
                                {r?.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </PanelErrorBoundary>
          </div>
        )}
      </div>
    </HubShell>
  );
}